import { useProcess } from '@fasstech/oip-starter-utils/app';
import { useFieldArray, useForm } from 'react-hook-form';
import { CollegeEnum } from '@fasstech/oip-core/entities/enums';
import useSaveSimulationDemographyMutation from './useSaveSimulationDemographyMutation.js';
import useGetSimulationDemographyOfferQuery from './useGetSimulationDemographyOfferQuery.js';
import { useEffect, useRef, useState } from 'react';
import { and, anyPass, filter, gt, includes, isEmpty, isNil, path, propOr } from 'ramda';
import {
  buildZoneOptions,
  canRemoveZone,
  emptyDemographyBlock,
  getCoverageZoneFlags,
  getSelectedZones,
  isAddZoneDisabled
} from '@@viewHelpers/coverageZone.js';
import {
  mapDemographiesToFormValues,
  mapFormValuesToOfferCheck,
  mapFormValuesToPayload
} from '../helpers/formValues.js';

const useSimulationDemographyForm = (
  getDemography,
  getMiddleText,
  getMiddleNumber,
  getMiddleBoolean,
  coverageZones
) => {
  const { processHandlers, context } = useProcess();
  const { onFormSubmit } = useSaveSimulationDemographyMutation();

  const coverageZoneFlags = getCoverageZoneFlags(
    getMiddleBoolean,
    getMiddleText
  );

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: mapDemographiesToFormValues(getDemography)
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'demographies',
    keyName: 'uuid'
  });

  const selectedZones = getSelectedZones(form.watch('demographies'));

  const { refetch: refetchOffer } = useGetSimulationDemographyOfferQuery({
    skip: true
  });
  const [hasNoOffer, setHasNoOffer] = useState(false);
  // Contrôle en échec (erreur réseau ou KO serveur) : on bloque sans afficher le message
  // « pas d'offre », puisqu'on ne sait pas s'il y en a une
  const [offerCheckFailed, setOfferCheckFailed] = useState(false);
  const [loadingOffer, setLoadingOffer] = useState(false);
  const lastOfferCheck = useRef(null);
  const offerCheck = mapFormValuesToOfferCheck(
    {
      college: form.watch('college'),
      demographies: form.watch('demographies')
    },
    { isMultiZone: coverageZoneFlags.isDisplayed }
  );
  const offerCheckKey = JSON.stringify(offerCheck);

  useEffect(() => {
    lastOfferCheck.current = offerCheckKey;

    if (isEmpty(offerCheck.zones)) {
      setHasNoOffer(false);
      setOfferCheckFailed(false);
      setLoadingOffer(false);
      return;
    }

    setLoadingOffer(true);
    refetchOffer(offerCheck)
      .then(({ data }) => {
        if (lastOfferCheck.current !== offerCheckKey) return;
        const hasOffer = path(
          ['getSimulationDemographyOffer_simulation_demography', 'hasOffer'],
          data
        );
        setHasNoOffer(hasOffer === false);
        setOfferCheckFailed(isNil(hasOffer));
      })
      .catch((err) => {
        console.error(
          'Erreur lors du contrôle d\'offre par zone de couverture :',
          err
        );
        if (lastOfferCheck.current !== offerCheckKey) return;
        setHasNoOffer(false);
        setOfferCheckFailed(true);
      })
      .finally(() => {
        if (lastOfferCheck.current === offerCheckKey) setLoadingOffer(false);
      });
  }, [offerCheckKey]);

  const handleAddZone = () => append(emptyDemographyBlock());

  const handleRemoveZone = (index) => {
    form.clearErrors(`demographies.${index}`);
    remove(index);

    setTimeout(() => {
      form.clearErrors('demographies');
      form.trigger();
    }, 150);
  };

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const { isValid } = form.formState;

  const collegeOptions = [
    {
      label: 'Ensemble du personnel',
      value: CollegeEnum.ALL
    },
    {
      label: 'Cadres',
      value: CollegeEnum.EXECUTIVE_EMPLOYEES
    },
    {
      label: 'Non cadres',
      value: CollegeEnum.EMPLOYEES
    }
  ];

  const filteredCollegeOptions = filter(
    ({ label }) => includes(label, propOr([], 'colleges', getDemography)),
    collegeOptions
  );

  const minEmployeeQuantity = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_MIN'
  );
  const maxEmployeeQuantity = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_MAX'
  );
  const minSpouseQuantity = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_MIN'
  );
  const maxSpouseQuantity = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_MAX'
  );
  const minChildrenQuantity = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_MIN'
  );
  const maxChildrenQuantity = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_MAX'
  );
  const minEmployeeAverageAge = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_MIN'
  );
  const maxEmployeeAverageAge = getMiddleNumber(
    'ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_MAX'
  );
  const employeeQuantityInfo = getMiddleText(
    'ENTREPRISE_DEMOGRAPHIE_NB_SALARIES_INFO',
    {
      minEmployeeQuantity,
      maxEmployeeQuantity
    }
  );
  const spouseQuantityInfo = getMiddleText(
    'ENTREPRISE_DEMOGRAPHIE_NB_CONJOINTS_INFO',
    {
      minSpouseQuantity,
      maxSpouseQuantity
    }
  );
  const childrenQuantityInfo = getMiddleText(
    'ENTREPRISE_DEMOGRAPHIE_NB_ENFANTS_INFO',
    {
      minChildrenQuantity,
      maxChildrenQuantity
    }
  );
  const employeeAverageAgeInfo = getMiddleText(
    'ENTREPRISE_DEMOGRAPHIE_AGE_MOYEN_SALARIE_INFO',
    {
      minEmployeeAverageAge,
      maxEmployeeAverageAge
    }
  );
  const tooltipText = (min, max, info) =>
    anyPass([
      () => and(gt(min, -1), gt(max, -1)),
      () => gt(max, -1),
      () => gt(min, -1)
    ])()
      ? info
      : undefined;

  const onSubmit = (values) => {
    onFormSubmit(
      mapFormValuesToPayload(values, {
        isMultiZone: coverageZoneFlags.isDisplayed
      }),
      (ok, err) => {
        if (ok && !err) {
          processHandlers.onNext({
            context: {
              projectId: context?.projectId
            }
          });
        }
      }
    );
  };

  return {
    form,
    fields,
    hasNoOffer,
    offerCheckFailed,
    loadingOffer,
    coverageZoneFlags,
    zoneOptionsAt: (index) =>
      buildZoneOptions(coverageZones, selectedZones, index),
    addZoneIsDisabled: isAddZoneDisabled(coverageZones, selectedZones),
    canRemoveZone,
    handleAddZone,
    handleRemoveZone,
    $onPrevClick,
    isValid,
    collegeOptions: filteredCollegeOptions,
    employeeCountTooltipText: tooltipText(
      minEmployeeQuantity,
      maxEmployeeQuantity,
      employeeQuantityInfo
    ),
    spouseCountTooltipText: tooltipText(
      minSpouseQuantity,
      maxSpouseQuantity,
      spouseQuantityInfo
    ),
    childrenCountTooltipText: tooltipText(
      minChildrenQuantity,
      maxChildrenQuantity,
      childrenQuantityInfo
    ),
    employeeAverageAgeTooltipText: tooltipText(
      minEmployeeAverageAge,
      maxEmployeeAverageAge,
      employeeAverageAgeInfo
    ),
    onSubmit
  };
};

export default useSimulationDemographyForm;
